import React, { useMemo } from 'react';
import { Gwei } from '../../lib/gwei';
import { divide } from '../../lib/math';
import { BaseSlider, BaseSliderProps, SliderConfig } from './BaseSlider';

interface StyledSliderProps extends BaseSliderProps {
  min?: bigint;
  max?: bigint;
  step?: bigint;
  tickSpacing?: bigint;
  minLabel?: string;
  maxLabel?: string;
}

export const StyledSlider: React.FC<StyledSliderProps> = ({
  value,
  min = 0n,
  max = Gwei.toGwei(1),
  step = Gwei.toGwei(0.01),
  tickSpacing,
  minLabel = '0%',
  maxLabel = '100%',
  ...baseProps
}) => {
  const config: SliderConfig = useMemo(() => {
    const range = max - min;
    const numberOfTicks =
      tickSpacing && range > 0n
        ? Math.max(2, Math.floor(Gwei.fromGwei(divide(range, tickSpacing))) + 1)
        : 5;

    const ticks = Array.from({ length: numberOfTicks }, (_, i) => {
      const percentage = numberOfTicks === 1 ? 0 : i / (numberOfTicks - 1);
      return {
        value: percentage,
        label: `${Math.round(percentage * 100)}%`,
      };
    });

    return {
      min: Gwei.fromGwei(min),
      max: Gwei.fromGwei(max),
      step: Gwei.fromGwei(step),
      ticks,
      normalizeValue: (val: bigint) => Gwei.fromGwei(val),
      denormalizeValue: (val: number) => Gwei.toGwei(val),
      getPercentage: (normalizedValue: number) => {
        const boundedValue = Math.max(
          Math.min(normalizedValue, Gwei.fromGwei(max)),
          Gwei.fromGwei(min)
        );
        const rangeValue = Gwei.fromGwei(max) - Gwei.fromGwei(min);
        return rangeValue > 0 ? (boundedValue - Gwei.fromGwei(min)) / rangeValue : 0;
      },
    };
  }, [min, max, step, tickSpacing]);

  return (
    <BaseSlider
      {...baseProps}
      value={value}
      config={config}
      minLabel={minLabel}
      maxLabel={maxLabel}
    />
  );
};
